import type { StaffCoupon, StaffCredit, StaffEnterpriseRow, StaffPlanSource } from '@/api'

/**
 * The Staff area's words for an enterprise's plan and billing (#394), shared by the list and an enterprise's page so
 * both say the same thing the same way.
 */

/** The date part of an ISO time: `2026-10-26`. Empty for none. */
export function dayOf(iso: string | null | undefined): string {
    return iso ? iso.slice(0, 10) : ''
}

/** Whole US dollars and cents from cents: `$2,500.00`. */
export function dollars(cents: number): string {
    return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** The billing state in a few words: `Past due`, `Trial until 2026-11-18`, `Quoted $2,500.00 a month`. */
export function billingWords(billing: StaffEnterpriseRow['billing']): string {
    switch (billing.state) {
        case 'active':
            return 'Active'
        case 'past_due':
            return 'Past due'
        case 'expired':
            return 'Expired'
        case 'comped':
            return 'Comped'
        case 'trial':
            return billing.trialEndsAt ? `Trial until ${dayOf(billing.trialEndsAt)}` : 'Trial'
        case 'quoted':
            return billing.quotedMonthlyPriceCents === null
                ? 'Quoted'
                : `Quoted ${dollars(billing.quotedMonthlyPriceCents)} a month`
        default:
            return 'No billing'
    }
}

/** How the plan was set, in words. */
export function planSourceWords(source: StaffPlanSource): string {
    return { flopay: 'FloPay', manual: 'Set by staff', none: 'Not set' }[source] ?? source
}

/** What a staff action was, in words: `set_plan_manual` reads `Set plan by hand`. */
export function staffActionWords(action: string): string {
    const words: Record<string, string> = {
        set_plan_manual: 'Set plan by hand',
        set_plan_flopay: 'Set plan through FloPay',
        extend: 'Extended',
        pause: 'Paused',
        unpause: 'Unpaused',
        close: 'Closed',
        cancel_close: 'Reopened',
        apply_coupon: 'Applied coupon',
        remove_coupon: 'Removed coupon',
        apply_credit: 'Applied credit',
        remove_credit: 'Removed credit',
    }
    return words[action] ?? action
}

/** A before or after of an action as `planId: free, endsAt: 2026-11-18`: what changed, never more. */
export function changeWords(change: Record<string, unknown> | null): string {
    if (!change) {
        return ''
    }
    return Object.entries(change)
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => `${key}: ${String(value)}`)
        .join(', ')
}

/** The staff actions on a coupon or a credit (#399), whose before and after name it rather than a field. */
const COUPON_AND_CREDIT_ACTIONS = new Set(['apply_coupon', 'remove_coupon', 'apply_credit', 'remove_credit'])

/**
 * A before or after of a staff action in words. A coupon or credit action (#399) names the coupon or the credit, and
 * a credit's amount in dollars, not cents: `Coupon cp_launch`, `Credit cr_1 of $50.00`. Any other action, or one whose
 * record names neither, says what changed as `changeWords` does.
 */
export function staffChangeWords(action: string, change: Record<string, unknown> | null): string {
    if (!change || !COUPON_AND_CREDIT_ACTIONS.has(action)) {
        return changeWords(change)
    }
    const words: string[] = []
    if (typeof change['couponId'] === 'string') {
        words.push(`Coupon ${change['couponId']}`)
    }
    if (typeof change['creditId'] === 'string') {
        words.push(`Credit ${change['creditId']}`)
    }
    if (typeof change['amountCents'] === 'number') {
        words.push(`of ${dollars(change['amountCents'])}`)
    }
    return words.length > 0 ? words.join(' ') : changeWords(change)
}

/** A coupon as staff tell it from another: its name and its discount, `Launch offer, 20% off for 3 months`. */
export function couponWords(coupon: Pick<StaffCoupon, 'name' | 'discount'>): string {
    return [coupon.name, coupon.discount].filter(part => part.trim() !== '').join(', ')
}

/** A credit as staff tell it from another: its amount and the day it was added, `$50.00, added 2026-10-01`. */
export function creditWords(credit: Pick<StaffCredit, 'amountCents' | 'createdAt'>): string {
    const day = dayOf(credit.createdAt)
    return day ? `${dollars(credit.amountCents)}, added ${day}` : dollars(credit.amountCents)
}

/**
 * What removing a coupon or a credit takes away, said before staff confirm it: the discount that stops, or what is
 * left of the credit, and how a coupon comes back, since a removal is not undone.
 */
export function removalWords(
    removing: { kind: 'coupon'; coupon: StaffCoupon } | { kind: 'credit'; credit: StaffCredit },
): string {
    if (removing.kind === 'coupon') {
        const { coupon } = removing
        return (
            `FloPay takes this coupon (${couponWords(coupon)}) off the subscription, and its discount no longer ` +
            `applies. This cannot be undone: to put it back, apply coupon ${coupon.id} again.`
        )
    }
    const { credit } = removing
    return (
        `FloPay takes this credit (${creditWords(credit)}) off the customer, so the ${dollars(credit.remainingCents)} ` +
        'left of it comes off no invoice. This cannot be undone: add a new credit instead. A credit FloPay has already ' +
        'used cannot be removed.'
    )
}

/**
 * A credit's amount as staff typed it in US dollars, in cents (`12.34` is 1234), or null when it is no amount a credit
 * can be: empty, not a number, 0 or less, or finer than a cent.
 */
export function creditCentsOf(typed: string | number | null | undefined): number | null {
    if (typed === null || typed === undefined || String(typed).trim() === '') {
        return null
    }
    const cents = Number(typed) * 100
    const whole = Math.round(cents)
    if (!Number.isFinite(cents) || whole <= 0 || Math.abs(cents - whole) > 1e-6) {
        return null
    }
    return whole
}
